import * as _ from "lodash";
import { BaselineBO } from "./BaselineBO";
import { ErrorMessage_restate } from "../PA.Core/Basic/MessageUtils";
import { WB_PartitionRRow, WB_PeriodRow, WB_ProductGroupRow } from "../PA.Core/PAdbSchemaRows";
import { CustomerHierarchyService } from "../PA.MD.Services/CustomerHierarchyService";
import { PeriodService } from "../PA.MD.Services/PeriodService";
import { ProductService } from "../PA.MD.Services/ProductService";
import { ProductHierarchyService } from "../PA.MD.Services/ProductHierarchyService";
import { Product_Hierarchy_Common_Levels } from "../PA.MD.Core/MD.Constants";
import { PASearch } from "../PA.Core/PASearch";

export class BaselineBOValidations {
    static async BeforeSave(aBO: BaselineBO): Promise<void> {}

    static async Validate(aBO: BaselineBO): Promise<void> {
        await BaselineBOValidations.BeforeSave(aBO);

        const emptyId: boolean =
            _.isEmpty(aBO.WB_Baseline.cif.BASELINE_ID) || _.isNull(aBO.WB_Baseline.cif.BASELINE_ID);
        ErrorMessage_restate(aBO.ValidateMessageProvider, emptyId, "emptyId", "Baseline ID cannot be empty");

        const emptyName: boolean =
            _.isEmpty(aBO.WB_Baseline.cif.BASELINE_NAME) || _.isNull(aBO.WB_Baseline.cif.BASELINE_NAME);
        ErrorMessage_restate(aBO.ValidateMessageProvider, emptyName, "emptyName", "Baseline Name cannot be empty");

        const emptySource: boolean =
            _.isEmpty(aBO.WB_Baseline.cif.BASELINE_SOURCE_ID) || _.isNull(aBO.WB_Baseline.cif.BASELINE_SOURCE_ID);
        ErrorMessage_restate(aBO.ValidateMessageProvider, emptySource, "emptySource", "Source cannot be empty");

        const checkSourcePA: boolean =
            aBO.WB_Baseline.cif.BASELINE_SOURCE_ID === "PA" ||
            aBO.WB_Baseline.cif.BASELINE_SOURCE_ID === "Performance Analyzer";
        const emptyBaselineType: boolean =
            !emptySource &&
            checkSourcePA &&
            (_.isEmpty(aBO.WB_Baseline.cif.BASELINE_TYPE_ID) || _.isNull(aBO.WB_Baseline.cif.BASELINE_TYPE_ID));
        ErrorMessage_restate(
            aBO.ValidateMessageProvider,
            emptyBaselineType,
            "emptyBaselineType",
            "Baseline Type cannot be empty",
        );

        if (aBO.partitionBO.WB_Partition?.WB_PartitionRRows?.length)
            await BaselineBOValidations.ValidateDistinctCustomerAndProductGroup(aBO);

        if (aBO.WB_Baseline.cif.BOM_DECOMP_IND == "1") await BaselineBOValidations.ValidateProductHierarchy(aBO);
    }

    static async ValidateDistinctCustomerAndProductGroup(aBO: BaselineBO): Promise<void> {
        const arrPartitionRRows: WB_PartitionRRow[] = aBO.partitionBO.WB_Partition.WB_PartitionRRows;
        if (_.isEmpty(_.filter(arrPartitionRRows, (r) => r.isActive))) return;

        const baselineCustHId = aBO.WB_Baseline.cif.WB_CUSTH_ID;
        const baselineCustomerLevel = aBO.WB_Baseline.cif.CUSTOMER_LEVEL;
        const baselinePrdHId = aBO.WB_Baseline.cif.WB_PRDH_ID;
        const baselineProductLevel = aBO.WB_Baseline.cif.PRODUCT_LEVEL;
        const baselineProductType = aBO.WB_Baseline.cif.PRODUCT_TYPE;

        const aPeriodRow: WB_PeriodRow = await PeriodService.getPeriodByWBID(aBO.WB_Baseline.cif.WB_PERIOD_ID);
        const baselineStartDt = aPeriodRow.PeriodStartDt;
        const baselineEndDt = aPeriodRow.PeriodEndDt;

        const custIdListAtOutputLevel: number[] = await CustomerHierarchyService.getWbIDsByCustomerType(
            baselineCustHId,
            baselineCustomerLevel,
        );

        const dctByCustIdPrdId: { [idCombo: string]: WB_PartitionRRow } = {};
        for (const aRow of arrPartitionRRows) {
            const custId = aRow.cif.WB_CUST_ID;
            const prdGrpId = aRow.cif.WB_PRDGRP_ID;

            let planningCustIds: number[];
            if (_.includes(custIdListAtOutputLevel, custId)) {
                planningCustIds = [custId];
            } else {
                const parent: number = await CustomerHierarchyService.getParent(
                    baselineCustHId,
                    custId,
                    baselineStartDt,
                    baselineEndDt,
                    baselineCustomerLevel,
                );
                if (!_.isUndefined(parent)) {
                    planningCustIds = [parent];
                } else {
                    planningCustIds = await CustomerHierarchyService.getChildren(
                        baselineCustHId,
                        custId,
                        baselineStartDt,
                        baselineEndDt,
                        baselineCustomerLevel,
                    );
                }
            }

            let productIds: number[];
            if (baselineProductType === "ProductGroup") {
                const prdGrpRow: WB_ProductGroupRow = await ProductService.getPrdGrpByWbID(prdGrpId);
                if (prdGrpRow.cif.LEVEL_ID === baselineProductLevel) {
                    productIds = await ProductHierarchyService.getChildren(
                        baselinePrdHId,
                        prdGrpId,
                        baselineStartDt,
                        baselineEndDt,
                        Product_Hierarchy_Common_Levels.PrdLevel,
                    );
                } else {
                    const parentId: number = await ProductHierarchyService.getPrdGrpParent(
                        baselinePrdHId,
                        prdGrpId,
                        baselineStartDt,
                        baselineEndDt,
                        baselineProductLevel,
                    );
                    if (!_.isUndefined(parentId)) {
                        productIds = await ProductHierarchyService.getChildren(
                            baselinePrdHId,
                            prdGrpId,
                            baselineStartDt,
                            baselineEndDt,
                            Product_Hierarchy_Common_Levels.PrdLevel,
                        );
                    } else {
                        const outputIdList: number[] = await ProductHierarchyService.getChildren(
                            baselinePrdHId,
                            prdGrpId,
                            baselineStartDt,
                            baselineEndDt,
                            baselineProductLevel,
                        );
                        productIds = [];
                        for (const outputId of outputIdList) {
                            const prdIds: number[] = await ProductHierarchyService.getChildren(
                                baselinePrdHId,
                                outputId,
                                baselineStartDt,
                                baselineEndDt,
                                Product_Hierarchy_Common_Levels.PrdLevel,
                            );
                            productIds.push(...prdIds);
                        }
                    }
                }
            } else {
                productIds = await ProductHierarchyService.getChildren(
                    baselinePrdHId,
                    prdGrpId,
                    baselineStartDt,
                    baselineEndDt,
                    Product_Hierarchy_Common_Levels.PrdLevel,
                );
            }

            _.forEach(planningCustIds, (planningCustId: number) => {
                _.forEach(productIds, (productId: number) => {
                    const id: string = `${planningCustId}-${productId}`;
                    if (id in dctByCustIdPrdId) {
                        return ErrorMessage_restate(
                            aBO.ValidateMessageProvider,
                            true,
                            "checkCustPrdGrpOverlap",
                            "Save Failed. Please ensure that the same Product Group is not selected across “Ship To” customers within a “Planning Account” customer.",
                        );
                    } else {
                        dctByCustIdPrdId[id] = aRow;
                    }
                });
            });
        }
        return;
    }

    static async ValidateProductHierarchy(aBO: BaselineBO): Promise<void> {
        const productHierarchyId = aBO.WB_Baseline.cif.WB_PRDH_ID;

        const filteredRes = await PASearch.find(
            "vUI_Prd_HierLevel_Entity_Rel",
            {
                WB_PRDH_ID: productHierarchyId,
            },
            {
                WB_PRDH_ID: { type: "int" },
            },
            [],
            1,
            [],
        );
        if (_.isEmpty(filteredRes)) {
            return ErrorMessage_restate(
                aBO.ValidateMessageProvider,
                true,
                "checkEntity",
                "“Decompose BOM” is only allowed for Product Hierarchies with Finished Product Leaf nodes, please deselect “Decompose BOM”, or choose a different Product Hierarchy.",
            );
        }

        const leafNodeEntityID = filteredRes[0].ENTITY_ID;
        if (leafNodeEntityID !== "FinishedProduct") {
            ErrorMessage_restate(
                aBO.ValidateMessageProvider,
                true,
                "checkEntity",
                "“Decompose BOM” is only allowed for Product Hierarchies with Finished Product Leaf nodes, please deselect “Decompose BOM”, or choose a different Product Hierarchy.",
            );
        }

        return;
    }
}
